"use client"

import { datadogRum } from "@datadog/browser-rum"
import { useSignup } from "@nelo/signup-lib"
import React, { useState } from "react"

import TextInput from "@modules/signup-and-order-flow/components/common/TextInput"
import { PIN_ENTRY_STEP_TEXTS } from "@modules/signup-and-order-flow/components/signup/constants"
import { StepComponentProps } from "@modules/signup-and-order-flow/components/signup/SignupFlowSteps"
import { FlowType, useSession } from "@modules/signup-and-order-flow/signup-and-order-flow-v2/session-context"

import AppDownloadStep from "./AppDownloadStep"
import StepWrapper from "../../common/StepWrapper"

const PinEntryStep: React.FC<StepComponentProps> = ({ onComplete, progressPercentage, onBack, stepId, treatments }) => {
  const { updateFlowType } = useSession()

  const pinEntryText = PIN_ENTRY_STEP_TEXTS
  const [showAppDownload, setShowAppDownload] = useState(false)

  const { pin, loginPin } = useSignup()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    pin.setTouched(true)
    pin.setBlurred(true)
    if (!pin.isValid) {
      return
    }

    try {
      const loginResponse = await loginPin.execute()
      onComplete(loginResponse.token, loginResponse.userUuid)
      updateFlowType(FlowType.LOG_IN)
    } catch (error) {
      console.error("Error logging in with PIN:", error)
      datadogRum.addError(error, {
        step: stepId,
        action: "loginPin",
      })
    }
  }

  if (showAppDownload) {
    return <AppDownloadStep onClose={() => setShowAppDownload(false)} />
  }

  return (
    <StepWrapper
      title={pinEntryText.title}
      subtitle={pinEntryText.subtitle}
      progressPercentage={progressPercentage}
      isLoading={loginPin.isLoading}
      formError={loginPin.error?.message}
      onSubmit={handleSubmit}
      submitDisabled={!pin.isValid || loginPin.isLoading}
      submitButtonText={pinEntryText.buttonText}
      showBackButton={true}
      onBack={onBack}
      footerText={pinEntryText.footerText}
    >
      <div>
        <div className="mb-1 flex items-center justify-between">
          <div></div>
          <button type="button" onClick={() => setShowAppDownload(true)} className="text-sm text-blue-600 hover:text-blue-800">
            Olvidé mi NIP
          </button>
        </div>
        <TextInput
          label={pinEntryText.pinLabel}
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          value={pin.value}
          onChange={(e) => pin.setValue(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
          onBlur={() => {
            pin.setTouched(true)
            pin.setBlurred(true)
            if (pin.errorMessage) {
              datadogRum.addError(new Error(`PIN validation error: ${pin.errorMessage}`), {
                step: stepId,
                field: "pin",
              })
            }
          }}
          onFocus={() => pin.setTouched(true)}
          isValid={pin.isValid}
          touched={pin.touched}
          blurred={pin.blurred}
          errorMessage={pin.errorMessage}
        />
      </div>
    </StepWrapper>
  )
}

export default PinEntryStep
